"use client";

import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn, scrollToId } from "@/lib/utils";
import Link from "next/link";
import { type FaqSectionProps } from "./types";

export default function FaqSection({
  faqs,
  description,
  heading = (
    <>
      Got questions?
      <br />
      We&apos;ve got answers.
    </>
  ),
  headingId = "faq-heading",
  headingClassName,
  hideContactLink = false,
  contactHref = "/contact",
  contactLabel = "Talk to our team",
  contactVariant = "button",
}: FaqSectionProps) {
  const prefersReducedMotion = useReducedMotion();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const openFromHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const index = faqs.findIndex((faq) => faq.id === hash);
      if (index === -1) return;
      setOpenIndex(index);
      scrollToId(hash);
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [faqs]);

  return (
    <section
      className="full-bleed band-wash py-section border-border border-b"
      aria-labelledby={headingId}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.6fr]">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.45 }}
          >
            <p className="text-brand mb-3 text-sm font-medium">FAQ</p>
            <h2
              id={headingId}
              className={cn(
                "text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl",
                headingClassName
              )}
            >
              {heading}
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">{description}</p>
            {!hideContactLink &&
              (contactVariant === "button" ? (
                <Link
                  href={contactHref}
                  className="bg-primary text-primary-foreground inline-flex items-center rounded-lg px-5 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
                >
                  {contactLabel}
                </Link>
              ) : (
                <Link
                  href={contactHref}
                  className="text-brand hover:text-brand/80 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
                >
                  {contactLabel}
                  <span aria-hidden="true">→</span>
                </Link>
              ))}
          </motion.div>

          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="space-y-3"
          >
            {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={faq.q}
                  id={faq.id}
                  className="border-border bg-card overflow-hidden rounded-xl border"
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    className="text-foreground flex w-full cursor-pointer items-center justify-between px-5 py-4 text-left text-sm font-semibold select-none"
                  >
                    {faq.q}
                    <span
                      className={`text-muted-foreground ml-4 shrink-0 transition-transform duration-300 ease-out ${isOpen ? "rotate-180" : ""}`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="size-4"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                  >
                    <div className="overflow-hidden">
                      <div className="text-muted-foreground px-5 pt-1 pr-10 pb-5 text-sm leading-relaxed">
                        {faq.a}
                        {faq.link && (
                          <a
                            href={faq.link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand hover:text-brand/80 mt-2 flex w-fit items-center gap-1.5 font-semibold transition-colors"
                          >
                            {faq.link.label}
                            <span aria-hidden="true">→</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
